import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUser } from '../../contexts/UserContext';
import { useMetaTags } from '../../hooks/useMetaTags';
import { api } from '../../utils/apiClient';

function ForgotPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isLoggedIn } = useUser();
  
  // Form state
  const [email, setEmail] = useState('');
  
  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // Refs
  const emailInputRef = useRef<HTMLInputElement>(null);
  
  // Set meta tags
  useMetaTags(
    t('auth.forgotPassword', 'Forgot Password'),
    t('auth.forgotPasswordDescription', 'Reset your password')
  );
  
  // Redirect if already logged in
  useEffect(() => {
    if (isLoggedIn) {
      navigate('/settings');
    }
  }, [isLoggedIn, navigate]);
  
  // Focus email input on load
  useEffect(() => {
    emailInputRef.current?.focus();
  }, []);
  
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Reset messages
    setFormError('');
    setSuccessMessage('');
    
    // Validate email
    if (!email || !email.includes('@')) {
      setFormError(t('auth.invalidEmail', 'Please enter a valid email address'));
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      console.log('Client: Sending password reset request for email:', email);
      
      // Call API to request password reset
      const response = await api.auth.requestPasswordReset(email);
      
      console.log('Client: Received response:', response);
      
      if (response.success) {
        setSuccessMessage(t(
          'auth.resetLinkSent', 
          'If your email exists in our system, you will receive a password reset link shortly.'
        ));
        setEmail('');
      } else {
        // For security reasons, we don't want to reveal if the email exists or not
        // So we show a success message regardless
        setSuccessMessage(t(
          'auth.resetLinkSent', 
          'If your email exists in our system, you will receive a password reset link shortly.'
        ));
      }
    } catch (error) {
      console.error('Password reset request error:', error);
      // Same as above, we show a generic success message
      setSuccessMessage(t(
        'auth.resetLinkSent', 
        'If your email exists in our system, you will receive a password reset link shortly.'
      ));
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-12 col-md-8 col-lg-6">
          <div className="card shadow-sm">
            <div className="card-body p-4">
              <h1 className="h4 mb-4 text-center">{t('auth.forgotPassword', 'Forgot Password')}</h1>
              
              {successMessage ? (
                <div className="alert alert-success" role="alert">
                  {successMessage}
                  <div className="mt-3">
                    <Link to="/login" className="btn btn-primary">
                      {t('auth.backToLogin', 'Back to Login')}
                    </Link>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-muted mb-4">
                    {t('auth.forgotPasswordInstructions', 'Enter your email address below and we\'ll send you a link to reset your password.')}
                  </p>
                  
                  {formError && (
                    <div className="alert alert-danger" role="alert">
                      {formError}
                    </div>
                  )}
                  
                  <form onSubmit={handleSubmit}>
                    {/* Email */}
                    <div className="mb-3">
                      <label htmlFor="email" className="form-label">
                        {t('auth.email', 'Email')}
                      </label>
                      <input
                        ref={emailInputRef}
                        type="email"
                        className="form-control"
                        id="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        required
                      />
                    </div>
                    
                    {/* Submit Button */}
                    <div className="d-grid mt-4">
                      <button 
                        type="submit" 
                        className="btn btn-primary" 
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            {t('common.loading', 'Loading...')}
                          </>
                        ) : (
                          t('auth.sendResetLink', 'Send Reset Link')
                        )}
                      </button>
                    </div>
                    
                    {/* Back to Login */}
                    <div className="mt-4 text-center">
                      <Link to="/login" className="text-primary">
                        {t('auth.backToLogin', 'Back to Login')}
                      </Link>
                    </div>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword; 